import { fetchStorageObjectBlob, usesStorageEmulator } from '@/firebase/storage-routing'
import * as Sentry from '@sentry/react'

export class MarkdownFetchError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'MarkdownFetchError'
  }
}

/**
 * Fetch a markdown file from GCS as a string.
 * Appends `.md` to the path if it isn't already there, validates that the blob
 * is one of the expected text content-types, and returns the decoded text.
 */
export async function fetchMarkdownFromGCS(bucket: string, filePath: string, legacy_always_cloud: boolean = true): Promise<string> {
  try {
    const mdPath = filePath.endsWith('.md') ? filePath : `${filePath}.md`

    const blob = await fetchStorageObjectBlob(bucket, mdPath, { legacy_always_cloud })

    const allowedTypes = ['text/plain', 'text/markdown', 'text/html']
    // Local-only allowance: the Storage emulator serves application/octet-stream
    // regardless of the stored contentType. Real GCS returns the correct text type.
    if (usesStorageEmulator({ legacy_always_cloud })) {
      allowedTypes.push('application/octet-stream')
    }
    if (!allowedTypes.includes(blob.type)) {
      throw new MarkdownFetchError(`Invalid blob type for markdown fetch: ${blob.type}`)
    }

    return await blob.text()
  } catch (error) {
    const fetchError = new MarkdownFetchError(`Failed to fetch markdown from bucket ${bucket}; File: ${filePath}: ${(error as Error).message}`)
    Sentry.captureException(fetchError, {
      extra: { error: JSON.stringify(error) },
    })
    throw fetchError
  }
}
